import { JWT_KEY, JWT_REFRESH_KEY } from "~/shared/apis/constant"
import { clearJWTToken, getJWTToken, saveJWTToken } from "~/shared/utils/jwt-token"
import axios, { AxiosError } from "axios"
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios"

export interface BaseResponse {
  message: string
  error?: string
  statusCode: number
}

let isRefreshing = false
let refreshTokenPromise: Promise<{
  accessToken: string
  refreshToken: string
}> | null = null

export const request: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    accept: "application/json",
  },
})

// 요청 인터셉터
request.interceptors.request.use(
  (config) => {
    const accessToken = window.sessionStorage.getItem(JWT_KEY)
    console.log(
      "%c[요청]%c %s",
      "color: #4CAF50; font-weight: bold;",
      "color: inherit;",
      `URL: ${config.url}`,
      config
    )

    if (
      config.url?.split("/")[1] === "check-inout" ||
      config.url?.split("/")[1] === "check-in" ||
      config.url?.split("/")[1] === "check-out"
    ) {
      return config
    }

    // 로그인/인증 관련 요청 처리
    if (config.url?.split("/")[1] === "auth") {
      if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
      return config
    }

    config.headers.Authorization = `Bearer ${accessToken}`
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 응답 인터셉터
request.interceptors.response.use(
  (response) => {
    console.log(
      "%c[응답]%c %s",
      "color:rgb(245, 214, 41); font-weight: bold;",
      "color: inherit;",
      `URL: ${response.config.url}`,
      response.data
    )
    return response
  },
  async (error: AxiosError<BaseResponse>) => {
    const originalRequest = error.config

    console.log(
      "%c[ERROR]%c %s",
      "color: #f44336; font-weight: bold;",
      "color: inherit;",
      `URL: ${error.config?.url}`,
      error.response?.data
    )

    // 401 에러 처리
    if (error.response?.data.statusCode === 401) {
      // 로그인 비밀번호 불일치
      if (error.response.data.message === "Invalid password") {
        return Promise.reject(error)
      }

      // 토큰이 만료된 경우
      if (error.response.data.error === "Unauthorized") {
        return handleExpiredToken(error)
      }

      // 토큰이 유효할 경우 (리프레시 요청)
      if (error.response.data.message === "Unauthorized") {
        return handleTokenRefresh(error, originalRequest)
      }

      // 기타 401 에러
      return handleUnknownAuthError(error)
    }

    // 네트워크 에러 처리
    return Promise.reject(
      error.code === "ERR_NETWORK" ? "허용되지 않은 네트워크 접근입니다." : error
    )
  }
)

// 토큰 만료 처리 함수
const handleExpiredToken = (error: AxiosError<BaseResponse>) => {
  clearJWTToken()
  // window.location.href = "/"
  // alert("토큰이 만료되었습니다. 다시 로그인 해주세요.")
  return Promise.reject(error)
}

// 토큰 리프레시 요청 함수
const refreshJWTTokens = async (): Promise<{
  accessToken: string
  refreshToken: string
}> => {
  const refreshToken = window.sessionStorage.getItem(JWT_REFRESH_KEY)

  if (!refreshToken) {
    throw new Error("No refresh token")
  }

  try {
    const response = await request.post(`/auth/refresh`)

    const { accessToken, refreshToken: newRefreshToken } = response.data

    // 새로운 토큰 저장
    saveJWTToken({
      accessToken,
      refreshToken: newRefreshToken,
    })

    return { accessToken, refreshToken: newRefreshToken }
  } catch (error) {
    clearJWTToken()
    throw error
  }
}

// 토큰 리프레시 처리 함수
const handleTokenRefresh = async (
  error: AxiosError<BaseResponse>,
  originalRequest?: InternalAxiosRequestConfig
) => {
  // 이미 리프레시 중인 경우
  if (isRefreshing && refreshTokenPromise) {
    try {
      const { accessToken } = await refreshTokenPromise
      if (originalRequest) {
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`
        return request(originalRequest)
      }
    } catch (refreshError) {
      return Promise.reject(refreshError)
    }
  }

  // 새로운 리프레시 시작
  if (!isRefreshing) {
    isRefreshing = true

    try {
      if (getJWTToken().accessToken === null || getJWTToken().refreshToken === null) {
        clearJWTToken()
        window.location.href = "/"
        alert("토큰이 존재하지 않습니다. 다시 로그인 해주세요.")
        return Promise.reject(error)
      }

      // 첫 번째 요청이 토큰 리프레시를 수행
      if (!refreshTokenPromise) {
        refreshTokenPromise = refreshJWTTokens()
      }

      // 다른 대기 중인 요청들은 토큰 리프레시 완료를 기다림
      const { accessToken } = await refreshTokenPromise

      if (originalRequest) {
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`
        return request(originalRequest)
      }
    } catch (refreshError) {
      clearJWTToken()
      return Promise.reject(refreshError)
    } finally {
      // 리프레시 상태 초기화
      isRefreshing = false
      refreshTokenPromise = null
    }
  }

  return Promise.reject(error)
}

// 기타 인증 에러 처리 함수
const handleUnknownAuthError = (error: AxiosError<BaseResponse>) => {
  clearJWTToken()
  window.location.href = "/"
  alert("인증에 문제가 발생했습니다. 다시 로그인 해주세요.")
  return Promise.reject(error)
}

export default request.request
